// 知识库右边的 AI 侧栏（照常见的 AI 侧栏做）：头部（灵智原生 AI、模型、历史、新对话）、空的时候一句问候 + 快捷指令、
// 回答（检索、引用角标、依据、复制）、输入框（选中的句子作为引用、范围「这一篇 / 整个知识库」、思考讨论、发送）。
// 范围默认是正在读的这一篇；没打开资料时是整个知识库。对话就是灵智原生的普通会话，这里记住哪几个是问这一篇的。

import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Dropdown, type MenuProps } from 'antd'
import { ArrowUp, AtSign, BookOpen, Check, ChevronDown, Clock, FileText, Lightbulb, ListChecks, Quote, Scale, Square, SquarePen, Trash2, X } from 'lucide-react'
import { createSession, listChatModels } from '../../api/chat'
import { CLAIM_KIND_LABEL, docInsights, extractClaims, type ClaimKind } from '../../api/insights'
import { modelLabel } from '../../api/settings'
import type { Knowledge, KnowledgeBase } from '../../api/types'
import { IconButton } from '../../ui'
import { AiAvatar } from '../../ui/AiAvatar'
import { docTitle } from './DocCard'
import { useDeleteClaim } from '../insights/useDeleteClaim'
import { Answer } from '../chat/Answer'
import { pickAgent, useAgents } from '../chat/Composer'
import { loadPrefs } from '../chat/prefs'
import { useChat } from '../chat/useChat'
import { useQuestionRouter } from '../chat/useRoute'
import { MicButton, useDictation } from '../voice/VoiceInput'

// ———— 记住哪几个对话是问这一篇（或这个库）的：只记在本机 ————
const CHATS_KEY = 'lingzhi-web.doc-chats'
interface Remembered {
  id: string
  title: string
  at: string
}
const readChats = (): Record<string, Remembered[]> => {
  try {
    return JSON.parse(localStorage.getItem(CHATS_KEY) ?? '{}') as Record<string, Remembered[]>
  } catch {
    return {}
  }
}
const remember = (key: string, chat: Remembered) => {
  try {
    const all = readChats()
    all[key] = [chat, ...(all[key] ?? []).filter((c) => c.id !== chat.id)].slice(0, 10)
    localStorage.setItem(CHATS_KEY, JSON.stringify(all))
  } catch {
    // 记不住也没关系，对话本身在灵智原生里
  }
}

/** 用户这句话前面带着选中的原文：「原文」+ 空行 + 问题。 */
const QUOTED = /^「([\s\S]+?)」\n\n([\s\S]*)$/
const withQuote = (quote: string | null, q: string) => (quote ? `「${quote}」\n\n${q}` : q)

/** 这一篇 / 整个库。 */
type ScopeKind = 'doc' | 'kb'

const SCOPE_NAME: Record<ScopeKind, string> = { doc: '这一篇', kb: '整个知识库' }

interface Ask {
  query: string
  scope: ScopeKind
}

export function KbAssistant({ kb, doc, quote, onClearQuote, onClose }: { kb?: KnowledgeBase; doc?: Knowledge; quote: string | null; onClearQuote: () => void; onClose?: () => void }) {
  const key = doc ? doc.id : `kb:${kb?.id ?? ''}`
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [pending, setPending] = useState<Ask | null>(null)
  const [scope, setScope] = useState<ScopeKind>(doc ? 'doc' : 'kb')
  const [card, setCard] = useState<'points' | 'claims' | null>(null)
  const [modelId, setModelId] = useState<string | undefined>(() => loadPrefs().modelId)
  const [starting, setStarting] = useState(false)
  const { message } = AntApp.useApp()

  // 换了一篇：回到这一篇的新对话
  useEffect(() => {
    setSessionId(null)
    setPending(null)
    setCard(null)
    setScope(doc ? 'doc' : 'kb')
  }, [key])

  const start = async (query: string, sc: ScopeKind = scope) => {
    setStarting(true)
    try {
      const title = doc ? `问「${docTitle(doc)}」` : `问「${kb?.name ?? '知识库'}」`
      const s = await createSession(title)
      remember(key, { id: s.id, title: query.replace(QUOTED, '$2').slice(0, 40), at: new Date().toISOString() })
      setPending({ query, scope: sc })
      setCard(null)
      setSessionId(s.id)
    } catch (e) {
      void message.error(e instanceof Error ? e.message : '没能开始对话')
    } finally {
      setStarting(false)
    }
  }

  const header = <AssistantHeader historyKey={key} modelId={modelId} onModel={setModelId} onPick={(id) => { setSessionId(id); setPending(null) }} onNew={() => { setSessionId(null); setPending(null); setCard(null) }} onClose={onClose} />
  const composerProps = { doc, kb, scope, onScope: setScope, quote, onClearQuote }

  if (sessionId && kb)
    return (
      <aside className="aib" aria-label="AI 侧栏">
        {header}
        <Thread key={sessionId} sessionId={sessionId} pending={pending} onSent={() => setPending(null)} modelId={modelId} {...composerProps} kb={kb} />
      </aside>
    )

  return (
    <aside className="aib" aria-label="AI 侧栏">
      {header}
      <div className="aib-body fade-edges">
        {card === 'points' && doc && <PointsCard doc={doc} onClose={() => setCard(null)} />}
        {card === 'claims' && doc && <ClaimsCard doc={doc} onClose={() => setCard(null)} />}
        {!card && (
          <div className="aib-hello">
            <AiAvatar size={64} live className="aib-orb" />
            <h2>{doc ? '有什么想问这篇的？' : `有什么想问「${kb?.name ?? '这个库'}」的？`}</h2>
          </div>
        )}
        <QuickActions doc={doc} onAsk={(q, sc) => void start(q, sc)} onCard={setCard} disabled={starting} />
        <History historyKey={key} onPick={(id) => setSessionId(id)} />
      </div>
      <AssistantComposer {...composerProps} busy={starting} onSend={(q) => void start(q)} />
    </aside>
  )
}

function AssistantHeader({ historyKey, modelId, onModel, onPick, onNew, onClose }: { historyKey: string; modelId?: string; onModel: (id: string | undefined) => void; onPick: (id: string) => void; onNew: () => void; onClose?: () => void }) {
  const models = useQuery({ queryKey: ['chat-models'], queryFn: listChatModels, staleTime: 5 * 60_000 }).data ?? []
  const current = models.find((m) => m.id === modelId)
  const chats = readChats()[historyKey] ?? []
  const modelMenu: MenuProps = {
    items: [{ key: '_', label: '默认模型' }, ...models.map((m) => ({ key: m.id, label: modelLabel(models, m) }))],
    selectable: true,
    selectedKeys: [modelId ?? '_'],
    onClick: ({ key }) => onModel(key === '_' ? undefined : key),
  }
  const historyMenu: MenuProps = {
    items: chats.length ? chats.map((c) => ({ key: c.id, label: c.title || '对话' })) : [{ key: '_', label: '还没有问过这一篇', disabled: true }],
    onClick: ({ key }) => key !== '_' && onPick(key),
  }
  return (
    <header className="aib-h">
      <AiAvatar size={20} />
      <b>灵智原生 AI</b>
      <Dropdown trigger={['click']} menu={modelMenu}>
        <button type="button" className="aib-model" aria-label="选模型">
          {current ? modelLabel(models, current) : '默认模型'}
          <ChevronDown width={13} height={13} aria-hidden="true" />
        </button>
      </Dropdown>
      <span className="grow" />
      <Dropdown trigger={['click']} menu={historyMenu}>
        <IconButton label="历史对话">
          <Clock />
        </IconButton>
      </Dropdown>
      <IconButton label="新对话" onClick={onNew}>
        <SquarePen />
      </IconButton>
      {/* 手机上 AI 侧栏是从底下拉出来的：收起 */}
      {onClose && (
        <IconButton label="收起 AI" className="sheet-x" onClick={onClose}>
          <X />
        </IconButton>
      )}
    </header>
  )
}

function QuickActions({ doc, onAsk, onCard, disabled }: { doc?: Knowledge; onAsk: (q: string, scope?: ScopeKind) => void; onCard: (c: 'points' | 'claims') => void; disabled: boolean }) {
  const claims = useQuery({ queryKey: ['doc-insights', doc?.id], enabled: !!doc, queryFn: () => docInsights(doc!.id) })
  const n = claims.data?.claims.length
  if (!doc)
    return (
      <div className="aib-qa" aria-label="快捷指令">
        <button type="button" disabled={disabled} onClick={() => onAsk('最近收进的几篇资料分别讲了什么？')}>
          <span className="ic"><BookOpen /></span>最近收进的讲了什么
        </button>
        <button type="button" disabled={disabled} onClick={() => onAsk('我的资料里，哪些主题收得最多？各举两篇。')}>
          <span className="ic"><ListChecks /></span>我收得最多的是哪些主题
        </button>
      </div>
    )
  return (
    <div className="aib-qa" aria-label="快捷指令">
      <button type="button" disabled={disabled} onClick={() => onAsk('用三句话总结这篇资料。')}>
        <span className="ic"><FileText /></span>总结这篇
      </button>
      {doc.description && (
        <button type="button" onClick={() => onCard('points')}>
          <span className="ic"><ListChecks /></span>要点
        </button>
      )}
      <button type="button" onClick={() => onCard('claims')}>
        <span className="ic"><Quote /></span>这篇的观点
        {n !== undefined && <span className="n">{n}</span>}
      </button>
      <button type="button" disabled={disabled} onClick={() => onAsk('这篇资料里，哪些说法证据不足、值得怀疑？请指出原文。')}>
        <span className="ic"><Scale /></span>哪些说法值得怀疑
      </button>
      <button type="button" disabled={disabled} onClick={() => onAsk('和我知识库里的其他资料相比，这篇有哪些相同和不同的看法？', 'kb')}>
        <span className="ic"><BookOpen /></span>和我收的其他资料比
      </button>
    </div>
  )
}

function History({ historyKey, onPick }: { historyKey: string; onPick: (id: string) => void }) {
  const chats = (readChats()[historyKey] ?? []).slice(0, 3)
  if (!chats.length) return null
  return (
    <section className="aib-hist" aria-label="之前问过的">
      <p className="lbl">之前问过的</p>
      {chats.map((c) => (
        <button key={c.id} type="button" onClick={() => onPick(c.id)}>
          <span>{c.title || '对话'}</span>
          <span className="at">{c.at.slice(5, 10).replace('-', '/')}</span>
        </button>
      ))}
    </section>
  )
}

/** 要点：灵智原生解析时整理的摘要，直接显示，不调用模型。 */
function PointsCard({ doc, onClose }: { doc: Knowledge; onClose: () => void }) {
  return (
    <section className="aib-card" aria-label="要点">
      <div className="hd">
        <b>要点</b>
        <span className="sub">灵智原生整理的摘要</span>
        <span className="grow" />
        <IconButton label="收起" onClick={onClose}>
          <X />
        </IconButton>
      </div>
      <p className="body">{doc.description}</p>
    </section>
  )
}

/** 这篇的观点：AI 整理的和自己记下的，按类型筛；还没整理过就可以让 AI 整理（会调用模型）。 */
function ClaimsCard({ doc, onClose }: { doc: Knowledge; onClose: () => void }) {
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const q = useQuery({ queryKey: ['doc-insights', doc.id], queryFn: () => docInsights(doc.id), refetchInterval: (x) => (x.state.data?.job?.status === 'running' ? 2000 : false) })
  const [kind, setKind] = useState<ClaimKind | 'all'>('all')
  // 折起来只留标题行；列表默认露 8 条，多的点「展开全部」
  const [folded, setFolded] = useState(false)
  const [all8, setAll8] = useState(false)
  const askDelete = useDeleteClaim(doc.id)
  const extract = useMutation({ mutationFn: () => extractClaims(doc.id), onSuccess: () => qc.invalidateQueries({ queryKey: ['doc-insights', doc.id] }), onError: (e) => void message.error(e.message) })
  const all = q.data?.claims ?? []
  const shown = all.filter((c) => kind === 'all' || c.kind === kind)
  const running = q.data?.job?.status === 'running'
  const left = q.data ? q.data.totalChunks - q.data.processedChunks : 0
  const SHOWN = 8
  return (
    <section className="aib-card" aria-label="这篇的观点" data-folded={folded}>
      <div className="hd">
        <button type="button" className="fold" aria-expanded={!folded} onClick={() => setFolded((v) => !v)}>
          <ChevronDown aria-hidden="true" />
          <b>这篇的观点</b>
          <span className="sub">{all.length} 条</span>
        </button>
        <span className="grow" />
        <IconButton label="关掉这张卡片" onClick={onClose}>
          <X />
        </IconButton>
      </div>
      {!folded && all.length > 0 && (
        <div className="kinds" role="tablist" aria-label="类型">
          {(['all', 'fact', 'opinion', 'inference'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}>
              {k === 'all' ? '全部' : CLAIM_KIND_LABEL[k]}
            </button>
          ))}
        </div>
      )}
      {!folded && (
        <ul className="cl">
          {shown.slice(0, all8 ? undefined : SHOWN).map((c) => (
            <li key={c.id}>
              <span>{c.text}</span>
              <span className="m">
                <span className="kind" data-kind={c.kind}>{CLAIM_KIND_LABEL[c.kind]}</span>
                {c.source === 'user' && <span className="mine">我记下的</span>}
              </span>
              <IconButton label={`删掉「${c.text.slice(0, 20)}」`} className="del" onClick={() => askDelete(c)}>
                <Trash2 width={13} height={13} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      {!folded && shown.length > SHOWN && (
        <button type="button" className="lnk more" onClick={() => setAll8((v) => !v)}>
          {all8 ? '收起' : `展开全部 ${shown.length} 条`}
        </button>
      )}
      {!folded && all.length === 0 && !running && <p className="body muted">还没有。读的时候选中一句「记为观点」，或者让 AI 整理一遍。</p>}
      {!folded && <div className="ft">
        {left > 0 && (
          <button type="button" className="lnk" disabled={running || extract.isPending} onClick={() => extract.mutate()}>
            <Lightbulb width={14} height={14} aria-hidden="true" />
            {running ? '正在整理…' : all.some((c) => c.source !== 'user') ? '接着整理' : '让 AI 整理观点'}
          </button>
        )}
        <span className="grow" />
        {all.length > 0 && (
          <Link className="lnk" to={`/insights?tab=claims&in=${doc.id}`}>
            在「观点」里管理
          </Link>
        )}
      </div>}
    </section>
  )
}

function Thread({ sessionId, kb, doc, pending, onSent, modelId, scope, onScope, quote, onClearQuote }: {
  sessionId: string
  kb: KnowledgeBase
  doc?: Knowledge
  pending: Ask | null
  onSent: () => void
  modelId?: string
  scope: ScopeKind
  onScope: (s: ScopeKind) => void
  quote: string | null
  onClearQuote: () => void
}) {
  const { turns, history, busy, send, stop } = useChat(sessionId)
  const agents = useAgents()
  const sent = useRef(false)
  const end = useRef<HTMLDivElement>(null)

  const route = useQuestionRouter([kb.id])
  /** wide：不缩小范围（「改查全部资料」）。 */
  const ask = (query: string, sc: ScopeKind = scope, wide = false) => {
    const agent = pickAgent(agents, loadPrefs().agentId)
    // 只问这一篇：只带这一篇，不带库（带上库就成了整个库）
    if (sc === 'doc' && doc) return send({ query, knowledgeBaseIds: [], knowledgeIds: [doc.id], agent, modelId })
    // 整个库：问题里说了在哪儿找（我写的、某个分区）就只在那几篇里找
    const prepare = wide ? undefined : () => route(query, [kb.id]).then((r) => r && { knowledgeBaseIds: r.knowledgeBaseIds, knowledgeIds: r.knowledgeIds, route: r.label })
    return send({ query, knowledgeBaseIds: [kb.id], knowledgeIds: [], agent, modelId, prepare })
  }

  // 第一句话：建好对话以后马上发，只发一次
  useEffect(() => {
    if (!pending || sent.current) return
    sent.current = true
    onSent()
    void ask(pending.query, pending.scope)
  }, [pending])

  const last = turns[turns.length - 1]
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' })
  }, [turns.length, last?.text.length])

  return (
    <>
      <div className="aib-body aib-thread fade-edges" aria-label="对话记录">
        {history.isError && <p className="aib-note">没能载入这个对话：{history.error.message}</p>}
        {turns.map((t, i) => {
          if (t.role === 'assistant') {
            const question = turns[i - 1]?.role === 'user' ? turns[i - 1].text : ''
            return (
              <div key={t.key} className="aib-ans">
                <div className="who">
                  <AiAvatar size={20} />
                  灵智原生 AI
                </div>
                <Answer turn={t} onWiden={question && !busy ? () => void ask(question, scope, true) : undefined} />
              </div>
            )
          }
          const m = t.text.match(QUOTED)
          return (
            <div key={t.key} className="aib-user">
              {m && (
                <span className="q">
                  <Quote width={12} height={12} aria-hidden="true" />
                  <span>{m[1]}</span>
                </span>
              )}
              <span className="bub">{m ? m[2] : t.text}</span>
            </div>
          )
        })}
        <div ref={end} />
      </div>
      <AssistantComposer doc={doc} kb={kb} scope={scope} onScope={onScope} quote={quote} onClearQuote={onClearQuote} busy={busy} onStop={stop} onSend={(q) => void ask(q)} placeholder="继续问…" />
    </>
  )
}

function AssistantComposer({ doc, kb, scope, onScope, quote, onClearQuote, busy, onSend, onStop, placeholder }: {
  doc?: Knowledge
  kb?: KnowledgeBase
  scope: ScopeKind
  onScope: (s: ScopeKind) => void
  quote: string | null
  onClearQuote: () => void
  busy: boolean
  onSend: (q: string) => void
  onStop?: () => void
  placeholder?: string
}) {
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const box = useRef<HTMLTextAreaElement>(null)
  const voice = useDictation({ value: text, onChange: setText, inputRef: box })
  useEffect(() => {
    if (quote) box.current?.focus()
  }, [quote])
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [text])

  const submit = async () => {
    if (busy) return
    const q = (voice.active ? await voice.stop() : text).trim()
    if (!q) return
    onSend(withQuote(quote, q))
    setText('')
    onClearQuote()
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
  }
  const tick = (k: ScopeKind) => <Check width={14} height={14} style={{ visibility: scope === k ? 'visible' : 'hidden' }} />
  const scopeMenu: MenuProps = {
    items: [
      ...(doc ? [{ key: 'doc', label: '这一篇', icon: tick('doc') }] : []),
      { key: 'kb', label: `整个「${kb?.name ?? '知识库'}」`, icon: tick('kb') },
    ],
    onClick: ({ key }) => onScope(key as ScopeKind),
  }
  // 思考讨论：把这句话（和正在读的这一篇）带去发起一场讨论
  const think = () => navigate('/', { state: { mode: 'think', topic: text.trim(), contextKnowledgeIds: doc ? [doc.id] : [] } })

  return (
    <div className="aib-foot">
      <form className="aib-composer" onSubmit={(e) => { e.preventDefault(); void submit() }}>
        {quote && (
          <div className="ctx">
            <span className="chip">
              <Quote width={12} height={12} aria-hidden="true" />
              <span>{quote}</span>
              <button type="button" aria-label="去掉引用" onClick={onClearQuote}>
                <X width={12} height={12} />
              </button>
            </span>
          </div>
        )}
        <label className="sr-only" htmlFor="aib-input">
          问 AI
        </label>
        <textarea id="aib-input" ref={box} rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} readOnly={voice.active} data-dictating={voice.active} placeholder={placeholder ?? (doc ? '问这一篇…' : '问这个知识库…')} />
        <div className="row">
          <Dropdown trigger={['click']} menu={scopeMenu}>
            <button type="button" className="chipbtn" aria-label="回答依据的范围">
              <AtSign width={13} height={13} aria-hidden="true" />
              {SCOPE_NAME[scope]}
              <ChevronDown width={12} height={12} aria-hidden="true" />
            </button>
          </Dropdown>
          <button type="button" className="chipbtn" onClick={think} title="几个视角拿资料辩一辩，最后由你下判断">
            <Lightbulb width={13} height={13} aria-hidden="true" />
            思考讨论
          </button>
          <span className="grow" />
          <MicButton d={voice} />
          {busy && onStop ? (
            <IconButton label="停下" onClick={onStop}>
              <Square />
            </IconButton>
          ) : (
            <IconButton label="发送" variant="primary" type="submit" disabled={(!text.trim() && !voice.active) || busy}>
              <ArrowUp />
            </IconButton>
          )}
        </div>
      </form>
      <p className="aib-disc">回答只依据选中的范围；点角标核对原文</p>
    </div>
  )
}
